import { useState, useEffect } from "react";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import api from "../api";

const SEVERITY_CONFIG = {
    1: { label: "Bloqueo", icon: "🚫", color: "text-red-600 bg-red-50" },
    2: { label: "Advertencia", icon: "⚠️", color: "text-yellow-600 bg-yellow-50" },
    3: { label: "Informativo", icon: "ℹ️", color: "text-blue-600 bg-blue-50" },
};

export default function RestrictionList({
    patientId,
    doctorId,
    onEdit,
    notify,
    refreshTrigger,
}) {
    const [restrictions, setRestrictions] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchRestrictions = async () => {
        setLoading(true);
        try {
            const params = {};
            if (patientId) params.patient_id = patientId;
            if (doctorId) params.doctor_id = doctorId;

            const response = await api.patientDoctorRestrictions.getMulti(
                null,
                null,
                null,
                null,
                params
            );
            setRestrictions(response.results || response);
        } catch (error) {
            console.error("Error al cargar restricciones:", error);
            notify?.("Error al cargar restricciones", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRestrictions();
    }, [patientId, doctorId, refreshTrigger]);

    const handleDelete = async (id) => {
        if (!window.confirm("¿Está seguro de eliminar esta restricción?")) return;

        try {
            await api.patientDoctorRestrictions.delete(id);
            notify?.("Restricción eliminada correctamente", "success");
            fetchRestrictions();
        } catch (error) {
            console.error("Error al eliminar restricción:", error);
            notify?.("Error al eliminar restricción", "error");
        }
    };

    const handleToggleActive = async (restriction) => {
        try {
            await api.patientDoctorRestrictions.update(
                restriction.id,
                { is_active: !restriction.isActive },
                true
            );
            notify?.(
                `Restricción ${!restriction.isActive ? "activada" : "desactivada"}`,
                "success"
            );
            fetchRestrictions();
        } catch (error) {
            console.error("Error al actualizar restricción:", error);
            notify?.("Error al actualizar restricción", "error");
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toLocaleDateString("es-PY", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (restrictions.length === 0) {
        return (
            <div className="text-center p-8 text-gray-500">
                <p>No hay restricciones registradas</p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {restrictions.map((restriction) => {
                const config = SEVERITY_CONFIG[restriction.severity];
                const isExpired =
                    restriction.expiresAt &&
                    new Date(restriction.expiresAt) < new Date();

                return (
                    <div
                        key={restriction.id}
                        className={`border rounded-lg p-4 ${
                            restriction.isActive && !isExpired
                                ? "border-gray-300"
                                : "border-gray-200 bg-gray-50 opacity-60"
                        }`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                    <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
                                    >
                                        <span className="mr-1">{config.icon}</span>
                                        {config.label}
                                    </span>
                                    {!restriction.isActive && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                                            Inactiva
                                        </span>
                                    )}
                                    {isExpired && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">
                                            Expirada
                                        </span>
                                    )}
                                </div>

                                <div className="space-y-1">
                                    {patientId && restriction.doctor && (
                                        <p className="text-sm font-medium text-gray-900">
                                            Dr. {restriction.doctor.firstName}{" "}
                                            {restriction.doctor.lastName}
                                        </p>
                                    )}
                                    {doctorId && restriction.patient && (
                                        <p className="text-sm font-medium text-gray-900">
                                            {restriction.patient.firstName}{" "}
                                            {restriction.patient.lastName}
                                        </p>
                                    )}
                                    <p className="text-sm text-gray-700">
                                        <span className="font-medium">Motivo:</span>{" "}
                                        {restriction.reason}
                                    </p>
                                    {restriction.notes && (
                                        <p className="text-sm text-gray-600">
                                            <span className="font-medium">Notas:</span>{" "}
                                            {restriction.notes}
                                        </p>
                                    )}
                                    <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
                                        <span>
                                            Creada: {formatDate(restriction.createdAt)}
                                        </span>
                                        {restriction.expiresAt && (
                                            <span>
                                                Expira: {formatDate(restriction.expiresAt)}
                                            </span>
                                        )}
                                        {restriction.createdBy && (
                                            <span>
                                                Por: {restriction.createdBy.firstName}{" "}
                                                {restriction.createdBy.lastName}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center space-x-2 ml-4">
                                <button
                                    onClick={() => handleToggleActive(restriction)}
                                    className="text-sm text-blue-600 hover:text-blue-800"
                                    title={
                                        restriction.isActive ? "Desactivar" : "Activar"
                                    }
                                >
                                    {restriction.isActive ? "Desactivar" : "Activar"}
                                </button>
                                <button
                                    onClick={() => onEdit?.(restriction)}
                                    className="p-1 text-gray-600 hover:text-blue-600"
                                    title="Editar"
                                >
                                    <PencilIcon className="h-5 w-5" />
                                </button>
                                <button
                                    onClick={() => handleDelete(restriction.id)}
                                    className="p-1 text-gray-600 hover:text-red-600"
                                    title="Eliminar"
                                >
                                    <TrashIcon className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
